import React from 'react'
import { List, useTable, ShowButton } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space, Select } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderStatus } from '../../theme/columns'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { CalendarDays, CalendarCheck, Ban, UsersRound, Video } from '../../components/icons'
import { formatMeetupStart, meetupVideoWindowStatus, type MeetupVideoWindowStatus } from './format'

type GroupMeetup = Tables<'group_meetups'>
type Group = Tables<'groups'>
type GroupMeetupRsvp = Tables<'group_meetup_rsvps'>
interface RsvpCounts {
  going: number
  maybe: number
  not_going: number
}

const FORMAT_LABEL: Record<string, string> = {
  in_person: 'In person',
  virtual: 'Video',
  hybrid: 'In person + video',
}

/** Same three statuses group_meetups.status's CHECK constraint allows, colored the way renderStatus/statusColor already handles 'scheduled' (green-ish) and 'cancelled' (red-ish) via the shared status palette. */
function formatLabel(format: string): string {
  return FORMAT_LABEL[format] ?? format
}

const VIDEO_WINDOW: Record<MeetupVideoWindowStatus, { label: string; tone: 'green' | 'gold' | 'neutral' | 'red' }> = {
  not_video: { label: '—', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'red' },
  upcoming: { label: 'Opens 15 min before', tone: 'neutral' },
  open: { label: 'Window open', tone: 'green' },
  closed: { label: 'Window closed', tone: 'neutral' },
}

/**
 * Meetups — every group's scheduled/cancelled in-person (or, once
 * groups_video_enabled, video) gatherings, across all groups. Filterable by
 * group, status, and "upcoming only". Cancel and "Promote to Event" live on
 * the Show page (show.tsx), not here — same split groups/{list,show}.tsx
 * already uses for status changes.
 */
export function GroupMeetupList() {
  const { tableProps, filters, setFilters } = useTable<GroupMeetup>({
    resource: 'group_meetups',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'starts_at', order: 'desc' }] },
  })
  const onRow = useRowShow('group_meetups')

  // Group filter — options from a bounded groups fetch (dozens to low
  // hundreds, not thousands), same pattern event_sessions/list.tsx uses for
  // its parent-event picker.
  const { result: groupsRes } = useList<Group>({
    resource: 'groups',
    sorters: [{ field: 'name', order: 'asc' }],
    pagination: { pageSize: 500 },
    queryOptions: { staleTime: 300_000 },
  })
  const groupOptions = (groupsRes?.data ?? []).map((g) => ({ label: g.name, value: g.id }))
  const activeGroupFilter = filters.find((f) => 'field' in f && f.field === 'group_id' && f.operator === 'eq')
  const activeGroupId = activeGroupFilter && 'value' in activeGroupFilter ? (activeGroupFilter.value as string) : undefined

  const activeStatusFilter = filters.find((f) => 'field' in f && f.field === 'status' && f.operator === 'eq')
  const activeStatus = activeStatusFilter && 'value' in activeStatusFilter ? (activeStatusFilter.value as string) : undefined

  const activeFormatFilter = filters.find((f) => 'field' in f && f.field === 'format' && f.operator === 'eq')
  const activeFormat = activeFormatFilter && 'value' in activeFormatFilter ? (activeFormatFilter.value as string) : undefined

  const activeUpcomingFilter = filters.find((f) => 'field' in f && f.field === 'ends_at' && f.operator === 'gte')
  const upcomingOnly = Boolean(activeUpcomingFilter)

  function setGroupFilter(groupId?: string) {
    const rest = filters.filter((f) => !('field' in f) || f.field !== 'group_id')
    setFilters(groupId ? [...rest, { field: 'group_id', operator: 'eq', value: groupId }] : rest, 'replace')
  }
  function setStatusFilter(status?: string) {
    const rest = filters.filter((f) => !('field' in f) || f.field !== 'status')
    setFilters(status ? [...rest, { field: 'status', operator: 'eq', value: status }] : rest, 'replace')
  }
  function setFormatFilter(format?: string) {
    const rest = filters.filter((f) => !('field' in f) || f.field !== 'format')
    setFilters(format ? [...rest, { field: 'format', operator: 'eq', value: format }] : rest, 'replace')
  }
  function setUpcomingFilter(on: boolean) {
    const rest = filters.filter((f) => !('field' in f) || f.field !== 'ends_at')
    setFilters(on ? [...rest, { field: 'ends_at', operator: 'gte', value: new Date().toISOString() }] : rest, 'replace')
  }

  // Resolve group_id -> name for ONLY the groups referenced on the current
  // page (bounded IN read), same pattern event_sessions/list.tsx uses for
  // event_id -> title.
  const groupIds = React.useMemo(
    () => Array.from(new Set(((tableProps.dataSource ?? []) as GroupMeetup[]).map((m) => m.group_id).filter(Boolean))),
    [tableProps.dataSource],
  )
  const { result: groupResult } = useList<Group>({
    resource: 'groups',
    filters: groupIds.length ? [{ field: 'id', operator: 'in', value: groupIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: groupIds.length > 0 },
  })
  const groupMap = React.useMemo<Record<string, string>>(
    () => Object.fromEntries(((groupResult?.data ?? []) as Group[]).map((g) => [g.id, g.name])),
    [groupResult],
  )

  // Going/maybe/declined counts per meetup — bounded to the current page's
  // meetup ids, one aggregate read (same "IN filter over the page's ids"
  // shape as the group name map above, not a query per row). RLS already
  // lets an admin read every group_meetup_rsvps row regardless of group
  // membership ("group_meetup_rsvps: admin write" is a `for all` policy,
  // which covers select too — no new migration needed for this), so this
  // is just a plain table read, same as the going-only version it replaces.
  const meetupIds = React.useMemo(
    () => ((tableProps.dataSource ?? []) as GroupMeetup[]).map((m) => m.id),
    [tableProps.dataSource],
  )
  const { result: rsvpResult } = useList<GroupMeetupRsvp>({
    resource: 'group_meetup_rsvps',
    filters: meetupIds.length ? [{ field: 'meetup_id', operator: 'in', value: meetupIds }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: meetupIds.length > 0 },
  })
  const rsvpCountMap = React.useMemo<Record<string, RsvpCounts>>(() => {
    const out: Record<string, RsvpCounts> = {}
    for (const r of (rsvpResult?.data ?? []) as GroupMeetupRsvp[]) {
      const bucket = (out[r.meetup_id] ??= { going: 0, maybe: 0, not_going: 0 })
      if (r.response === 'going') bucket.going += 1
      else if (r.response === 'maybe') bucket.maybe += 1
      else if (r.response === 'not_going') bucket.not_going += 1
    }
    return out
  }, [rsvpResult])

  // Summary deck — three bounded pageSize:1 count queries, unfiltered by the
  // page's own filters (same shape as the Groups list's stat deck).
  const scheduledQ = useList<GroupMeetup>({
    resource: 'group_meetups',
    filters: [{ field: 'status', operator: 'eq', value: 'scheduled' }],
    pagination: { pageSize: 1 },
  })
  const upcomingQ = useList<GroupMeetup>({
    resource: 'group_meetups',
    filters: [
      { field: 'status', operator: 'eq', value: 'scheduled' },
      { field: 'ends_at', operator: 'gte', value: new Date().toISOString() },
    ],
    pagination: { pageSize: 1 },
  })
  const cancelledQ = useList<GroupMeetup>({
    resource: 'group_meetups',
    filters: [{ field: 'status', operator: 'eq', value: 'cancelled' }],
    pagination: { pageSize: 1 },
  })

  const scheduled = scheduledQ.result?.total ?? 0
  const upcoming = upcomingQ.result?.total ?? 0
  const cancelled = cancelledQ.result?.total ?? 0

  const stats: Stat[] = [
    { label: 'Scheduled', value: scheduled.toLocaleString(), icon: CalendarDays, tone: scheduled > 0 ? 'gold' : 'neutral' },
    { label: 'Upcoming', value: upcoming.toLocaleString(), icon: CalendarCheck, tone: upcoming > 0 ? 'green' : 'neutral' },
    { label: 'Cancelled', value: cancelled.toLocaleString(), icon: Ban, tone: cancelled > 0 ? 'red' : 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        description:
          'Every in-person (and, once video is on, virtual) gathering organizers have scheduled across all groups. Open one to cancel it or promote it to a full Event.',
      }}
      stats={stats}
      statColumns={3}
    >
      <List canCreate={false} title={false} breadcrumb={false}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
          <Space size={8}>
            <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}>Group</span>
            <Select
              aria-label="Filter by group"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All groups"
              style={{ minWidth: 220 }}
              options={groupOptions}
              value={activeGroupId}
              onChange={(value?: string) => setGroupFilter(value)}
            />
          </Space>
          <Space size={8}>
            <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}>Status</span>
            <Select
              aria-label="Filter by status"
              allowClear
              placeholder="All statuses"
              style={{ minWidth: 160 }}
              options={[
                { label: 'Scheduled', value: 'scheduled' },
                { label: 'Cancelled', value: 'cancelled' },
              ]}
              value={activeStatus}
              onChange={(value?: string) => setStatusFilter(value)}
            />
          </Space>
          <Space size={8}>
            <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}>Format</span>
            <Select
              aria-label="Filter by format"
              allowClear
              placeholder="All formats"
              style={{ minWidth: 180 }}
              options={[
                { label: 'In person', value: 'in_person' },
                { label: 'Video', value: 'virtual' },
                { label: 'In person + video', value: 'hybrid' },
              ]}
              value={activeFormat}
              onChange={(value?: string) => setFormatFilter(value)}
            />
          </Space>
          <Space size={8}>
            <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}>When</span>
            <Select
              aria-label="Filter by upcoming"
              style={{ minWidth: 140 }}
              options={[
                { label: 'All time', value: 'all' },
                { label: 'Upcoming only', value: 'upcoming' },
              ]}
              value={upcomingOnly ? 'upcoming' : 'all'}
              onChange={(value: string) => setUpcomingFilter(value === 'upcoming')}
            />
          </Space>
        </div>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<GroupMeetup>
              dataIndex="group_id"
              title="Group"
              render={(groupId: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>
                  {groupMap[groupId] ?? groupId}
                </span>
              )}
            />
            <Table.Column<GroupMeetup>
              dataIndex="title"
              title="Title"
              render={(value: string) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}>{value}</span>
              )}
            />
            <Table.Column<GroupMeetup>
              dataIndex="format"
              title="Format"
              render={(value: string) => <Pill tone="jaguar">{formatLabel(value)}</Pill>}
            />
            <Table.Column<GroupMeetup>
              dataIndex="starts_at"
              title="Starts"
              sorter
              render={(_: string, record: GroupMeetup) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.ink }}>
                  {formatMeetupStart(record)}
                </span>
              )}
            />
            <Table.Column<GroupMeetup> dataIndex="status" title="Status" render={renderStatus} />
            <Table.Column<GroupMeetup>
              title="Video"
              dataIndex="id"
              render={(_: string, record: GroupMeetup) => {
                const windowStatus = meetupVideoWindowStatus(record)
                const { label, tone } = VIDEO_WINDOW[windowStatus]
                if (windowStatus === 'not_video') {
                  return <span style={{ color: brand.sub }}>{label}</span>
                }
                return (
                  <Space size={4}>
                    <Video size={13} color={brand.sub} />
                    <Pill tone={tone}>{label}</Pill>
                  </Space>
                )
              }}
            />
            <Table.Column<GroupMeetup>
              title="RSVPs"
              dataIndex="id"
              render={(id: string) => {
                const counts = rsvpCountMap[id] ?? { going: 0, maybe: 0, not_going: 0 }
                return (
                  <Space size={10}>
                    <Space size={4}>
                      <UsersRound size={13} color={brand.sub} />
                      <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>
                        {counts.going.toLocaleString()} going
                      </span>
                    </Space>
                    <span style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}>
                      {counts.maybe.toLocaleString()} maybe
                    </span>
                    <span style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub }}>
                      {counts.not_going.toLocaleString()} can’t
                    </span>
                  </Space>
                )
              }}
            />
            <Table.Column<GroupMeetup>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: GroupMeetup) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
